// ==========================================================================
// Plyr HTML5 helpers
// ==========================================================================

import support from './support';
import { toggleClass, createElement, removeElement } from './utils/elements';
import { on, triggerEvent } from './utils/events';
import is from './utils/is';
import { silencePromise } from './utils/promise';
import { setAspectRatio } from './utils/style';
import { getDate } from './utils/time';
import browser from './utils/browser';
import i18n from './utils/i18n';
import ui from './ui';
import { fetch as fetchPolyfill } from 'whatwg-fetch'

function getAbsoluteURL(url) {
  // Check if absolute URL
  if (!url.match(/^https?:\/\//)) {
    const div = document.createElement('div')
    div.innerHTML = `<a href="${url}">x</a>`
    url = div.firstChild.href
  }
  return url
}

function getEventDateStyle(date) {
  const format = (value) => `0${value}`.slice(-2);

  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hours = date.getHours();
  const mins = date.getMinutes();
  const secs = date.getSeconds();

  return `${year}${format(month)}${format(day)}${format(hours)}${format(mins)}${format(secs)}`;
}

function uuidv4() {
  const uuid = ([1e7]+-1e3+-4e3+-8e3+-1e11).replace(/[018]/g, c =>
    (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
  );
  return '{' + uuid + '}'
  // return '{' + self.crypto.randomUUID() + '}'
}

const html5 = {

  showMessage(message, passive = true) {
    if (passive && this.message) {
      return Promise.reject(new Error('message already display'));
    }

    if (this.elements.message) {

      ui.togglePoster.call(this, false);

      this.elements.message.removeAttribute('hidden');
      this.elements.text.innerHTML = message;
      toggleClass(this.elements.container, this.config.classNames.messageEnabled, true);
    }
  },

  getSources() {
    if (!this.isHTML5) {
      return [];
    }

    const sources = Array.from(this.media.querySelectorAll('source'));

    // Filter out unsupported sources (if type is specified)
    return sources.filter((source) => {
      const type = source.getAttribute('type');

      if (is.empty(type)) {
        return true;
      }

      return support.mime.call(this, type);
    });
  },

  // Get quality levels
  getQualityOptions() {
    // Whether we're forcing all options (e.g. for streaming)
    if (this.config.quality.forced) {
      return this.config.quality.options;
    }

    // Get sizes from <source> elements
    return html5.getSources
      .call(this)
      .map((source) => Number(source.getAttribute('size')))
      .filter(Boolean);
  },

  setup() {
    if (!this.isHTML5) {
      return;
    }

    const player = this;
    player.stars.tick = 0;
    player.stars.sentUnloadEvent = false;
    player.stars.sentBeginContentEvent = false;

    const playerId = player.storage.get('playerId');
    if (!!playerId) {
      player.stars.playerId = playerId;
    } else {
      player.stars.playerId = uuidv4()
      player.storage.set({playerId: player.stars.playerId})
    }

    if (player.dash && browser.isSafari) {
      html5.showMessage.call(player, i18n.get('canNotSupportSafari', player.config.starplayer));
      return;
    }

    // if (browser.isFirefox) {
    //   html5.cancelRequests.call(player);
    //   html5.showMessage.call(player, i18n.get('canNotSupportSafari', player.config.starplayer));
    //   return;
    // }


    if (html5.watermarkEnabled.call(player)) {
      on.call(player, player.media, 'playing', (event) => {
        player.debug.log(event.type);
        if (!is.element(player.elements.watermark)) {
          html5.watermarkRender.call(player);
        }
      });

      on.call(player, player.media, 'ended emptied abort error', (event) => {
        player.debug.log(event.type);
        html5.watermarkRelease.call(player);
      });
    }

    function initPlayingEvent() {
      if (player.source == getAbsoluteURL(this.config.blankVideo)) {
        player.debug.log(player.source);
        return;
      }

      player.stars.sentUnloadEvent = false;

      player.debug.log('>==play', player.stars.sentUnloadEvent);

      clearInterval(player.stars.playingContentEventTimer);

      if (player.stars.tick == 0) {
        player.stars.tick = new Date().getTime();
      }
      player.stars.playingContentEventTimer = setInterval(() => {
        html5.ContentEvent.call(player, 'playing_content', (txt) => {
          player.playingContentEvent(txt);
        });
      }, player.config.starplayer.eventInterval * 1000);
    }

    on.call(player, player.media, 'loadeddata', () => {
      if (player.source == getAbsoluteURL(this.config.blankVideo)) {
        player.debug.log(player.source);
        return;
      }


      if (!player.stars.currentUrl && player.source.indexOf('blob:', 0) == 0) {
        let src = player.source;
        let sourceElement = player.media.querySelector('source');
        if (is.element(sourceElement)) {
          src = sourceElement.getAttribute('src');
        } else {
          sourceElement = player.media.querySelector('sourcex');
          if (is.element(sourceElement)) {
            src = sourceElement.getAttribute('src');
          }
        }
        player.stars.currentUrl = src;
        player.stars.contentId = sourceElement.getAttribute('contentid') || '';
      }

      player.stars.beginEventTimer = setTimeout(() => {
        html5.beginContentEvent.call(player);
        player.stars.sentBeginContentEvent = true;
      }, 500);
    });

    on.call(player, player.media, 'play', () => {
      initPlayingEvent.call(this);
    });

    on.call(player, player.media, 'pause', () => {
      if (player.source == getAbsoluteURL(this.config.blankVideo)) {
        player.debug.log(player.source);
        return;
      }
      if (player.stars.sentBeginContentEvent == true) {
        clearInterval(player.stars.playingContentEventTimer);
        html5.ContentEvent.call(player, 'playing_content', (txt) => {
          player.playingContentEvent(txt);
          player.stars.tick = 0;
        });
      }
    });
  

    on.call(player, player.media, 'ended emptied abort error', () => {

      player.debug.log('>==ended emptied abort error', player.stars.sentUnloadEvent);

      if (player.stars.sentUnloadEvent == true) {
        return;
      }

      if (player.source == getAbsoluteURL(this.config.blankVideo)) {
        player.debug.log(player.source);
        return;
      }
      if (player.stars.sentBeginContentEvent == true) {
        clearInterval(player.stars.playingContentEventTimer);
        html5.ContentEvent.call(player, 'end_content', (txt)=> {
          player.endContentEvent(txt);
        });
      }
    });  

    window.addEventListener('pagehide', (e)=> {
      player.debug.log('>==pagehide', player.stars.sentUnloadEvent);

      if (player.stars.sentUnloadEvent == true) {
        return;
      }

      if (player.source == getAbsoluteURL(this.config.blankVideo)) {
        player.debug.log(player.source);
        return;
      }

      if (player.stars.sentBeginContentEvent == true) {
        clearInterval(player.stars.playingContentEventTimer);
        html5.ContentEvent.call(player, 'end_content', (txt)=> {
          player.endContentEvent(txt);
        });
      }

      return;
    })

    // Set speed options from config
    player.options.speed = player.config.speed.options; //[0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 4]

    // Set aspect ratio if fixed
    if (!is.empty(this.config.ratio)) {
      setAspectRatio.call(player);
    }

    // Quality
    Object.defineProperty(player.media, 'quality', {
      get() {
        // Get sources
        const sources = html5.getSources.call(player);
        const source = sources.find((s) => s.getAttribute('src') === player.source);

        // Return size, if match is found
        return source && Number(source.getAttribute('size'));
      },
      set(input) {
        // forced + onChange는 starplyr.js set quality에서 직접 호출하므로 여기서는 skip
        if (player.config.quality.forced && is.function(player.config.quality.onChange)) {
          // onChange는 이미 호출됨 — qualitychange 이벤트만 발생
        } else if (player.quality === input) {
          return;
        } else {
          // Get sources
          const sources = html5.getSources.call(player);
          // Get first match for requested size
          const source = sources.find((s) => Number(s.getAttribute('size')) === input);

          // No matching source found
          if (!source) {
            return;
          }

          player.elements.inputs.seek.disabled = true;
          // Get current state
          const { currentTime, paused, preload, readyState, playbackRate } = player.media;

          player.stars.contentId = source.getAttribute('contentid') || '';
          player.stars.currentUrl = source.getAttribute('src'); 

          // Set new source
          player.media.src = source.getAttribute('src');

          // Prevent loading if preload="none" and the current source isn't loaded (#1044)
          if (preload !== 'none' || readyState) {
            // Restore time
            player.once('loadedmetadata', () => {
              player.elements.inputs.seek.disabled = false;
              player.speed = playbackRate;
              player.currentTime = currentTime;

              // Resume playing
              if (!paused) {
                silencePromise(player.play());
              }
            });

            // Load new source
            player.media.load();
          }
        }

        // Trigger change event
        triggerEvent.call(player, player.media, 'qualitychange', false, {
          quality: input,
        });
      },
    });
  },

  // Cancel current network requests
  // See https://github.com/sampotts/plyr/issues/174
  cancelRequests() {
    if (!this.isHTML5) {
      return;
    }

    const player = this;
    clearTimeout(player.stars.beginEventTimer);
    clearInterval(player.stars.playingContentEventTimer);

    // Remove child sources
    removeElement(html5.getSources.call(this));

    // Set blank video src attribute
    // This is to prevent a MEDIA_ERR_SRC_NOT_SUPPORTED error
    // Info: http://stackoverflow.com/questions/32231579/how-to-properly-dispose-of-an-html5-video-and-close-socket-or-connection
    this.media.setAttribute('src', this.config.blankVideo);

    // Load the new empty source
    // This will cancel existing requests
    // See https://github.com/sampotts/plyr/issues/174
    this.media.load();

    // Debugging
    this.debug.log('Cancelled network requests');
  },

  watermarkEnabled() {
    const player = this;
    return player.isVideo && player.config.starplayer.watermark.enabled;
  },

  setTimer(callback, interval) {
    if (interval <= 0) {
      return setTimeout(callback, 0);
    } else {
      return setInterval(callback, interval);
    }
  },

  clearTimer(timer, interval) {
    if (interval <= 0) {
      clearTimeout(timer);
    } else {
      clearInterval(timer);
    }
  },

  watermarkDisplay(displayDuration) {
    const player = this;
    if (player.elements.watermark == null) {
      return;
    }
    if (!player.elements.watermark.parentNode) {
      if (!is.element(player.elements.watermark)) {
        player.elements.watermark = createElement('div');
      }
      player.elements.wrapper.appendChild(player.elements.watermark);
    }

    player.elements.watermark.innerHTML = player.config.starplayer.watermark.text;
    let style = player.elements.watermark.style;
    style.position = 'absolute';
    style.width = '100%';
    style.display = 'block';
    style.margin = '0';
    style.padding = '0';
    style.textTransform = 'uppercase';
    style.fontWeight = 'bold';
    
    if (browser.isAndroid || browser.isIos) {
      style.fontSize = '100%';
    } else {
      style.fontSize = '150%';
    }
    style.color = player.config.starplayer.watermark.color;
    style.opacity = player.config.starplayer.watermark.opacity;
    style.filter = 'opacity(' + (style.opacity * 100) + '%)';
    style.zIndex = '1';

    let textAlign = 'initial';
    if (player.config.starplayer.watermark.horizontalAlign.toLowerCase() == 'random') {
      switch (Math.floor(Math.random() * 10000) % 3) {
        case 0:
          textAlign = 'left';
          break;
        case 1:
          textAlign = 'center';
          break;
        case 2:
          textAlign = 'right';
          break;
      }
      style.textAlign = textAlign;
    } else {
      style.textAlign = player.config.starplayer.watermark.horizontalAlign.toLowerCase();
    }
    
    const rect = player.elements.container.getBoundingClientRect();
    let h;
    const middle = rect.height / 2;
    const mediaRatio = player.media.videoWidth / player.media.videoHeight;
    const clientRatio = rect.width / rect.height;
    if (mediaRatio >= clientRatio) {
      h = rect.width / mediaRatio / 2;
    } else {
      h = middle;
    }

    var watermarkHeight = player.elements.watermark.clientHeight;

    let top;
    if (player.config.starplayer.watermark.verticalAlign.toLowerCase() == 'random') {
      switch (Math.floor(Math.random() * 10000) % 3) {
        case 0:
          top = middle - h;
          break;
        case 1:
          top = middle - (watermarkHeight / 2);
          break;
        case 2:
          top = middle + h - watermarkHeight;
          if (player.config.starplayer.watermark.displayTime) {
            player.elements.watermark.innerHTML = getDate(new Date());
          }
          break;
      }
    } else {
      switch (player.config.starplayer.watermark.verticalAlign.toLowerCase()) {
        case "top":
          top = middle - h;
          break;
        case "center":
          top = middle - (watermarkHeight / 2);
          break;
        case "bottom":
          top = middle + h - watermarkHeight;
          break;
      }
    }
    style.top = `${top}px`;

    player.stars.watermarkDurationTimer = setTimeout(() => {
      style.display = 'none';
    }, displayDuration * 1000 - 100);
  },

  watermarkRender() {
    const player = this;
    player.elements.watermark = createElement('div');
    player.elements.wrapper.appendChild(player.elements.watermark);

    let interval = player.config.starplayer.watermark.positionChangeInterval;
    // const intervalIsValid = is.number(interval) && interval > 0;
    // interval = intervalIsValid ? interval : 5;

    let displayDuration = player.config.starplayer.watermark.displayDuration;
    const displayDurationIsValid = is.number(displayDuration) && displayDuration < interval;
    displayDuration = displayDurationIsValid ? displayDuration : interval;
    if (interval <= 0) {
      displayDuration = 10800;
    }

    if (player.stars.watermarkTimer) {
      // clearInterval(player.stars.watermarkTimer);
      html5.clearTimer.call(player, player.stars.watermarkTimer, interval);
    }

    if (player.stars.watermarkDurationTimer) {
      clearTimeout(player.stars.watermarkDurationTimer);
    }

    html5.watermarkDisplay.call(player, displayDuration);

    if (interval > 0) {
      player.stars.watermarkTimer = setInterval(() => {
        html5.watermarkDisplay.call(player, displayDuration);
      }, interval * 1000);
    }
  },

  watermarkRelease() {
    const player = this;
    if (player.elements.watermark) {
      player.elements.watermark.remove();
      player.elements.watermark = null;
    }

    if (player.stars.watermarkTimer) {
      // clearInterval(player.stars.watermarkTimer);
      const interval = player.config.starplayer.watermark.positionChangeInterval;
      html5.clearTimer.call(player, player.stars.watermarkTimer, interval);
    }

    if (player.stars.watermarkDurationTimer) {
      clearTimeout(player.stars.watermarkDurationTimer);
    }
  },

  beginContentEvent() {
    const player = this;
    const userId = player.config.starplayer.userId;
    if (is.url(player.config.starplayer.eventUrl) && userId != 'ANONYMOUS') {
      let data = new FormData();
      data.append('event', "begin_content");
      data.append('user_id', userId);
      data.append('device_id', player.stars.playerId);
      data.append('os_version', browser.isOsx ? "macOS" : "windows");
      data.append('app_version', "0");
      data.append('content_id', player.stars.contentId);
      data.append('content_url', encodeURI(player.stars.currentUrl));
      data.append('play_type', "download");
      data.append('state', "normal");
      data.append('license', player.config.starplayer.app.license);
      data.append('date', getEventDateStyle(new Date()));
      data.append('online', "yes");
      //data.append('format', player.config.starplayer.responseType);

      fetchPolyfill(player.config.starplayer.eventUrl, {
        method: 'POST',
        headers: {
          'format': player.config.starplayer.responseType,
        },
        body: data
      }).then((response) => {
        return response.text()
      }).then((str) => {
        player.beginContentEvent(str);
      }).catch((e) => {
        player.debug.log(e);
      });
    }
  },

  ContentEvent(event, callback) {
    const player = this;
    const userId = player.config.starplayer.userId;
    if (is.url(player.config.starplayer.eventUrl) && userId != 'ANONYMOUS') {
      let data = new FormData();
      data.append('event', event);
      data.append('user_id', userId);
      data.append('device_id', player.stars.playerId);
      data.append('os_version', browser.isOsx ? "macOS" : "windows");
      data.append('app_version', "0");
      data.append('content_id', player.stars.contentId);
      data.append('content_url', encodeURI(player.stars.currentUrl));
      data.append('play_type', "download");
      data.append('license', player.config.starplayer.app.license);
      let time = player.tracker.getPlayedTime();
      //let playedTime = time - player.stars.playedTime;
      //player.stars.playedTime = time;
      data.append('play_time', time);
      data.append('play_time2', time * 1000);
      let currentTick = new Date().getTime();
      if (player.stars.tick == 0) {
        player.stars.tick = currentTick;
      }
      let diff = currentTick - player.stars.tick;
      const maximumLimit = 300000;
      if (diff < 0 || diff > maximumLimit) {
        diff = 180000;
      }
      player.stars.tick = currentTick;
      data.append('latest_playtime', parseInt(diff / 1000, 10));
      data.append('latest_playtime2', diff);
      const speed = player.speed;
      data.append('latest_ratio_playtime', parseInt(diff * speed.toFixed(1) / 1000, 10));
      data.append('content_duration', parseInt(player.duration, 10));
      data.append('current_position', parseInt(player.currentTime, 10));

      data.append('date', getEventDateStyle(new Date()));
      data.append('online', "yes");
      data.append('play_ratio', speed.toFixed(1));
      //data.append('format', player.config.starplayer.responseType);

      data.append('tracker_time', parseInt(player.tracker.getTrackerTime(), 10));
      data.append('tracker_data', player.tracker.getTracker());

      if (event == 'end_content') {
        const result = navigator.sendBeacon(player.config.starplayer.eventUrl, data);
        if (result && is.function(callback)) {
          callback("");
        }
        player.stars.sentUnloadEvent = true;
      } else {
        fetchPolyfill(player.config.starplayer.eventUrl, {
          method: 'POST',
          headers: {
            'format': player.config.starplayer.responseType,
          },
          body: data
        }).then((response) => {
          return response.text()
        }).then((str) => {
          //player.playingContentEvent(str);
          if (is.function(callback)) {
            callback(str);
          }
        }).catch((e) => {
          player.debug.log(e);
        });
      }

    }
  },
};

export default html5;
